"use client";

import React, { useState, useRef, useEffect } from "react";
import { Sun, Moon, Monitor, ChevronDown } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

export function ThemeToggle() {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getThemeIcon = () => {
    if (theme === "auto") {
      return <Monitor className="w-4 h-4" />;
    } else if (theme === "light") {
      return <Sun className="w-4 h-4" />;
    } else {
      return <Moon className="w-4 h-4" />;
    }
  };

  const getThemeLabel = () => {
    if (theme === "auto") {
      return `Auto (${resolvedTheme === "dark" ? "Dark" : "Light"})`;
    } else if (theme === "light") {
      return "Light";
    } else {
      return "Dark";
    }
  };

  const handleThemeChange = (newTheme: "light" | "dark" | "auto") => {
    setTheme(newTheme);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-2 rounded-lg bg-white/10 border border-theme hover:bg-white/20 transition-colors duration-200"
        aria-label="Toggle theme"
      >
        {getThemeIcon()}
        <span className="text-xs sm:text-sm font-medium">{getThemeLabel()}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 bottom-full mt-2 w-48 bg-white/20 backdrop-blur-[8px] border border-white/20 rounded-lg shadow-lg z-50">
          <div className="py-2 px-1 gap-1 flex flex-col">
            <button
              onClick={() => handleThemeChange("auto")}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:rounded-lg hover:bg-[var(--color-bg-inverse-paper)]/10 transition-all duration-200 ${
                theme === "auto"
                  ? "bg-primary-20 text-primary"
                  : "text-korrero-gray-light"
              }`}
            >
              <Monitor className="w-4 h-4" />
              <span>Auto</span>
            </button>
            <button
              onClick={() => handleThemeChange("light")}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:rounded-lg hover:bg-[var(--color-bg-inverse-paper)]/10 transition-all duration-200 ${
                theme === "light"
                  ? "bg-primary-20 text-primary rounded-lg"
                  : "text-korrero-gray-light"
              }`}
            >
              <Sun className="w-4 h-4" />
              <span>Light</span>
            </button>

            <button
              onClick={() => handleThemeChange("dark")}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:rounded-lg hover:bg-[var(--color-bg-inverse-paper)]/10 transition-all duration-200 ${
                theme === "dark"
                  ? "bg-primary-20 text-primary"
                  : "text-korrero-gray-light"
              }`}
            >
              <Moon className="w-4 h-4" />
              <span>Dark</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
